import { useEffect, useRef, useState } from 'react';

// =========================================================
// Version polling hook
// =========================================================
//
// Detects when a new version of the UI has been deployed while the
// user's browser session is still running the old one. The hook is
// wired up once near the root of the tree (see `EslManager.tsx`);
// the returned `updateAvailable` flag is consumed by the update
// banner component.
//
// This is the backoffice counterpart to the worker UI's
// `src/helper/useVersionPoll.ts`. Behaviour and endpoint contract
// are identical; the backoffice variant does not share its baseline
// via a module-level store because the backoffice ships as a single
// monolithic bundle (no code-splitting, no chunk-load failures, no
// error boundary that needs to read the baseline on demand). The
// baseline lives in a ref internal to the hook.
//
// Design decisions worth noting for future readers:
//
//   - Detection only, no reload. The user initiates the reload by
//     tapping the banner.
//
//   - False-positive safety. A failed fetch never flips the flag;
//     only a successful response with a *different* non-empty
//     release does. "I can't reach the server" and "a new version
//     is live" look the same at the network layer and we must not
//     conflate them.
//
//   - Cache-busting is belt-and-suspenders. `cache: 'no-store'` +
//     server-side no-store headers are already enough in theory;
//     appending `?t=${Date.now()}` makes caching physically
//     impossible under any intermediary that keys on URL.
//
//   - Multiple triggers. 30s timer for the steady state, window
//     `online` for reconnects, document `visibilitychange` for tab
//     re-focus (timers get throttled in the background), and
//     `pageshow` with `event.persisted` for browser bfcache restore
//     where in-memory React state is reinstated wholesale.
//
//   - Once flipped, stop. `updateAvailable === true` is terminal;
//     timers and listeners tear down to avoid wasted polling on a
//     session that's just waiting for the user to reload.

const DEFAULT_INTERVAL_MS = 30_000;
const FETCH_TIMEOUT_MS = 5_000;

interface VersionResponse {
  release?: unknown;
}

export interface VersionPollResult {
  updateAvailable: boolean;
}

export default function useVersionPoll(endpoint: string, intervalMs: number = DEFAULT_INTERVAL_MS): VersionPollResult {
  const [updateAvailable, setUpdateAvailable] = useState<boolean>(false);
  const baselineRef = useRef<string | null>(null);

  useEffect(() => {
    if (updateAvailable) {
      return;
    }

    let cancelled = false;

    const probe = async (): Promise<void> => {
      if (cancelled) {
        return;
      }

      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => {
        controller.abort();
      }, FETCH_TIMEOUT_MS);

      try {
        const separator = endpoint.includes('?') ? '&' : '?';
        const bustedUrl = `${endpoint}${separator}t=${Date.now()}`;

        const response = await fetch(bustedUrl, {
          cache: 'no-store',
          signal: controller.signal,
        });
        if (cancelled) {
          return;
        }
        if (!response.ok) {
          return;
        }

        const data = (await response.json()) as VersionResponse;
        const release = typeof data.release === 'string' ? data.release : '';
        if (release === '') {
          return;
        }

        if (baselineRef.current === null) {
          // First successful response: establish the session baseline.
          baselineRef.current = release;
          return;
        }
        if (release !== baselineRef.current) {
          setUpdateAvailable(true);
        }
      } catch {
        // Transient failure — not evidence of a deploy. Retry next tick.
      } finally {
        window.clearTimeout(timeoutId);
      }
    };

    void probe();

    const timerId = window.setInterval(() => {
      void probe();
    }, intervalMs);

    const handleVisibility = (): void => {
      if (document.visibilityState === 'visible') {
        void probe();
      }
    };
    const handleOnline = (): void => {
      void probe();
    };
    const handlePageShow = (event: PageTransitionEvent): void => {
      if (event.persisted) {
        void probe();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('online', handleOnline);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      cancelled = true;
      window.clearInterval(timerId);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [endpoint, intervalMs, updateAvailable]);

  return { updateAvailable };
}
